import {
  DEFAULT_INSPECTION_TEMPERATURE_UNIT,
  InspectionTemperature,
  inspectionTemperatureUnits,
} from 'pages/reporting/reports/rug/rugReportData'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

type LegacyInspectionTemperatureUnit = 'Fahrenheit' | 'Celsius'

const LEGACY_UNIT_MAP: Record<string, GeckoMeasurementUnit> = {
  Fahrenheit: GeckoMeasurementUnit.Fahrenheit,
  fahrenheit: GeckoMeasurementUnit.Fahrenheit,
  Celsius: GeckoMeasurementUnit.Celsius,
  celsius: GeckoMeasurementUnit.Celsius,
}

const isInspectionTemperatureUnit = (
  unit: unknown
): unit is GeckoMeasurementUnit =>
  inspectionTemperatureUnits.some(
    (candidate) =>
      candidate === unit ||
      (typeof unit === 'object' &&
        unit !== null &&
        'id' in unit &&
        unit.id === candidate.id)
  )

const getInspectionTemperatureUnit = (unit: unknown): GeckoMeasurementUnit => {
  if (isInspectionTemperatureUnit(unit)) {
    return unit
  }

  if (typeof unit === 'string') {
    return (
      inspectionTemperatureUnits.find((candidate) => candidate.id === unit) ??
      LEGACY_UNIT_MAP[unit] ??
      DEFAULT_INSPECTION_TEMPERATURE_UNIT
    )
  }

  return DEFAULT_INSPECTION_TEMPERATURE_UNIT
}

const getLegacyInspectionTemperatureUnit = (
  unit: unknown
): LegacyInspectionTemperatureUnit =>
  getInspectionTemperatureUnit(unit) === GeckoMeasurementUnit.Celsius
    ? 'Celsius'
    : 'Fahrenheit'

export const normalizeInspectionTemperature = (
  temperature: unknown,
  unit?: unknown
): InspectionTemperature | undefined => {
  if (temperature instanceof GeckoMeasurement) {
    return GeckoMeasurement.CreateFromValue(
      temperature.num,
      getInspectionTemperatureUnit(temperature.unit)
    )
  }

  if (typeof temperature === 'number') {
    if (!Number.isFinite(temperature)) return undefined

    return GeckoMeasurement.CreateFromValue(
      temperature,
      getInspectionTemperatureUnit(unit)
    )
  }

  if (typeof temperature === 'string') {
    const trimmedTemperature = temperature.trim()
    if (!trimmedTemperature) return undefined

    const parsedTemperature = Number(trimmedTemperature)
    if (!Number.isFinite(parsedTemperature)) return trimmedTemperature

    return GeckoMeasurement.CreateFromValue(
      parsedTemperature,
      getInspectionTemperatureUnit(unit)
    )
  }

  return undefined
}

export const serializeInspectionTemperatureForForm = (
  temperature: InspectionTemperature | undefined
) => {
  if (!temperature) return undefined

  if (typeof temperature === 'string') {
    return {
      value: temperature,
      unit: DEFAULT_INSPECTION_TEMPERATURE_UNIT.id,
    }
  }

  return {
    value: String(temperature.num),
    unit: getInspectionTemperatureUnit(temperature.unit).id,
  }
}

export const serializeInspectionTemperatureForStorage = (
  temperature: InspectionTemperature | undefined
) => {
  if (!temperature) {
    return {
      inspectionTemperature: '',
      inspectionTemperatureUnit: getLegacyInspectionTemperatureUnit(
        DEFAULT_INSPECTION_TEMPERATURE_UNIT
      ),
    }
  }

  if (typeof temperature === 'string') {
    return {
      inspectionTemperature: temperature,
      inspectionTemperatureUnit: getLegacyInspectionTemperatureUnit(
        DEFAULT_INSPECTION_TEMPERATURE_UNIT
      ),
    }
  }

  return {
    inspectionTemperature: String(temperature.num),
    inspectionTemperatureUnit: getLegacyInspectionTemperatureUnit(
      temperature.unit
    ),
  }
}

export const convertInspectionTemperature = (
  temperature: InspectionTemperature | undefined,
  targetUnit: GeckoMeasurementUnit
): InspectionTemperature | undefined => {
  if (!temperature || typeof temperature === 'string') return temperature
  return temperature.convertTo(targetUnit)
}

export const formatInspectionTemperature = (
  temperature: InspectionTemperature | undefined
) => {
  if (!temperature) return ''

  if (typeof temperature === 'string') {
    return temperature
  }

  return `${temperature.num}${getInspectionTemperatureUnit(temperature.unit).name}`
}
